/**
 * Dynamic endpoint generator for RTK Query
 * Simplified version of restohub-frontend pattern for widget use
 *
 * RTK Query's dynamic endpoint builder requires flexible param/result types
 * that cannot be statically typed when endpoints are generated from config.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { apiEndpoints, type ApiEndpoint } from './endpointsConfig'

/**
 * Creates endpoint name from entity name and action
 * e.g., ('menuConfig', 'get') => 'getMenuConfig'
 */
const createEndpointName = (entityName: string, action: string): string => {
  const capitalizedEntity = entityName.charAt(0).toUpperCase() + entityName.slice(1)
  return `${action.toLowerCase()}${capitalizedEntity}`
}

/**
 * Creates cache tags for an endpoint
 */
const createCacheTags = (entityName: string, endpoint: ApiEndpoint): string[] => {
  const entityTag = entityName.charAt(0).toUpperCase() + entityName.slice(1)
  const tags = [entityTag]

  if (endpoint.tags) {
    tags.push(...endpoint.tags)
  }

  return tags
}

/**
 * Replaces path parameters like {id} with actual values
 */
const buildPath = (path: string, params: Record<string, any> = {}): string => {
  let finalPath = path
  Object.entries(params).forEach(([key, value]) => {
    if (path.includes(`{${key}}`) && value !== undefined && value !== null) {
      finalPath = finalPath.replace(`{${key}}`, String(value))
    }
  })
  return finalPath
}

/**
 * Builds query string from params (excludes path params)
 */
const buildQueryString = (path: string, params: Record<string, any> = {}): string => {
  const queryParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && !path.includes(`{${key}}`)) {
      queryParams.append(key, String(value))
    }
  })
  return queryParams.toString()
}

/**
 * Creates all RTK Query endpoints from config
 */
export const createEndpoints = (builder: any) => {
  const endpoints: any = {}

  apiEndpoints.forEach((endpoint) => {
    const { name: entityName, path, actions } = endpoint
    const entityTag = entityName.charAt(0).toUpperCase() + entityName.slice(1)

    // GET - Single item query
    if (actions.includes('GET')) {
      const endpointName = createEndpointName(entityName, 'get')

      endpoints[endpointName] = builder.query({
        query: (arg: any) => {
          const params = typeof arg === 'object' ? arg : { id: arg }
          let finalPath = buildPath(path, params)

          // Append id if not in path template
          if (params.id && !path.includes('{id}')) {
            finalPath = `${finalPath}${params.id}/`
          }

          const queryString = buildQueryString(path, params)
          return `${finalPath}${queryString ? `?${queryString}` : ''}`
        },
        providesTags: (_result: any, _error: any, arg: any) => {
          const tags = createCacheTags(entityName, endpoint)
          const id = typeof arg === 'object' ? arg.id : arg
          return id ? [{ type: entityTag, id }, ...tags] : tags
        },
      })
    }

    // LIST - Collection query
    if (actions.includes('LIST')) {
      const endpointName = createEndpointName(entityName, 'list')

      endpoints[endpointName] = builder.query({
        query: (arg: any = {}) => {
          const finalPath = buildPath(path, arg)
          const queryString = buildQueryString(path, arg)
          return `${finalPath}${queryString ? `?${queryString}` : ''}`
        },
        providesTags: (result: any) => {
          const tags = createCacheTags(entityName, endpoint)
          // Handle paginated responses
          if (result?.results) {
            return [
              ...tags,
              ...result.results.map((item: any) => ({ type: entityTag, id: item.id })),
            ]
          }
          // Handle array responses
          if (Array.isArray(result)) {
            return [
              ...tags,
              ...result.map((item: any) => ({ type: entityTag, id: item.id })),
            ]
          }
          return tags
        },
      })
    }

    // POST - Create mutation
    if (actions.includes('POST')) {
      const endpointName = createEndpointName(entityName, 'add')

      endpoints[endpointName] = builder.mutation({
        query: (args: any = {}) => {
          const finalPath = buildPath(path, args)

          // Separate query params from body
          const queryParams = new URLSearchParams()
          const bodyParams: any = {}

          Object.entries(args).forEach(([key, value]) => {
            if (value !== undefined && value !== null && !path.includes(`{${key}}`)) {
              bodyParams[key] = value
              // restaurant_id often goes in query string
              if (key === 'restaurant_id') {
                queryParams.append(key, String(value))
              }
            }
          })

          const queryString = queryParams.toString()
          return {
            url: `${finalPath}${queryString ? `?${queryString}` : ''}`,
            method: 'POST',
            body: bodyParams,
          }
        },
        invalidatesTags: () => createCacheTags(entityName, endpoint),
      })
    }

    // PATCH - Partial update mutation
    if (actions.includes('PATCH')) {
      const endpointName = createEndpointName(entityName, 'patch')

      endpoints[endpointName] = builder.mutation({
        query: (args: any = {}) => {
          let finalPath = buildPath(path, args)

          // Append id if not in path template
          if (args.id && !path.includes('{id}')) {
            finalPath = `${finalPath}${args.id}/`
          }

          const queryParams = new URLSearchParams()
          if (args.restaurant_id) {
            queryParams.append('restaurant_id', String(args.restaurant_id))
          }

          // Exclude id and restaurant_id from body
          const bodyParams = Object.fromEntries(
            Object.entries(args).filter(([key]) => key !== 'id' && key !== 'restaurant_id')
          )

          const queryString = queryParams.toString()
          return {
            url: `${finalPath}${queryString ? `?${queryString}` : ''}`,
            method: 'PATCH',
            body: bodyParams,
          }
        },
        invalidatesTags: (_result: any, _error: any, args: any) => {
          const tags = createCacheTags(entityName, endpoint)
          return args?.id ? [{ type: entityTag, id: args.id }, ...tags] : tags
        },
      })
    }

    // PUT - Full update mutation
    if (actions.includes('PUT')) {
      const endpointName = createEndpointName(entityName, 'update')

      endpoints[endpointName] = builder.mutation({
        query: ({ id, ...body }: { id: string | number; [key: string]: any }) => ({
          url: `${path}${id}/`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_result: any, _error: any, { id }: { id: string | number }) => {
          const tags = createCacheTags(entityName, endpoint)
          return [{ type: entityTag, id }, ...tags]
        },
      })
    }

    // DELETE - Remove mutation
    if (actions.includes('DELETE')) {
      const endpointName = createEndpointName(entityName, 'delete')

      endpoints[endpointName] = builder.mutation({
        query: (args: any = {}) => {
          let finalPath = buildPath(path, args)

          // Append id if not in path template
          if (args.id && !path.includes('{id}')) {
            finalPath = `${finalPath}${args.id}/`
          }

          const queryParams = new URLSearchParams()
          if (args.restaurant_id) {
            queryParams.append('restaurant_id', String(args.restaurant_id))
          }

          const queryString = queryParams.toString()
          return {
            url: `${finalPath}${queryString ? `?${queryString}` : ''}`,
            method: 'DELETE',
          }
        },
        invalidatesTags: () => createCacheTags(entityName, endpoint),
      })
    }
  })

  return endpoints
}
